import { motion } from "framer-motion";
import { FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";

import { CloseButton } from "@/components/AlertDialogs/Dialog";
import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import Text from "@/components/Text";
import DistributionSchedulesTipsField from "./DistributionSchedulesTipsField";
import { ReleaseResource } from "@/types/resources";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const Dialog = styled(motion.div)`
  width: 384px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
`;

const DialogContent = styled.div`
  padding: 56px 32px 40px;
`;

const Heading = styled(Text)`
  margin-bottom: 16px;
`;

const VideoTitle = styled(Text)`
  margin-bottom: 24px;
`;

const DialogFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  margin-top: 40px;
`;

export interface FormDataT {
  tips: { name: string; external_id: string; id: number }[];
}

interface Props {
  onClose: () => void;
  release: Pick<ReleaseResource, "tips" | "id" | "title">;
}

const DistributionScheduleTipsModal: FC<Props> = ({ onClose, release }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      tips:
        release.tips?.map((t) => ({
          id: t.id,
          external_id: t.external_id,
          name: t.name,
        })) ?? [],
    },
  });

  const onSubmit = handleSubmit(async ({ tips }) => {
    try {
      await api.put(`/v1/releases/${release.id}/tips`, {
        tips: tips.map((a) => a.id),
      });

      await queryClient.invalidateQueries(["releases", { id: release.id }]);

      queryClient.invalidateQueries(["releases", { videoSummary: true }]);

      onClose();

      alerts.success("Video recipe successfully updated", release.title);
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <DialogContent as="form" onSubmit={onSubmit}>
          <Heading $type="heading2">Assign Tip</Heading>
          <VideoTitle>{release.title}</VideoTitle>
          <DistributionSchedulesTipsField control={control} />
          <DialogFooter>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
            >
              Confirm
            </Button>
            <Button color="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Modal>
  );
};

export default DistributionScheduleTipsModal;
